import React, { useContext } from "react";
import CruiseCard from "components/CruiseCard";
import { formatCurrency } from "src/utils/localization/formatCurrency";
import GeolocationContext from "src/utils/localization/geolocation/GeolocationContext";
import ErrorBoundary from "components/ErrorBoundary";
import { computeWorldLocationLabel } from "src/utils/worldLocationLabel";
import { AlgoliaCruiseHit, AlgoliaCruiseHitImage } from "types/algolia";
import { useLanguage } from "src/utils/localization/languages/I18nContext";

export interface CruiseCardResultProps {
  hit: AlgoliaCruiseHit;
  showDestination: boolean;
}

const CruiseCardResult: React.FC<CruiseCardResultProps> = ({
  hit,
  ...otherProps
}) => {
  const language = useLanguage();
  const geolocation = useContext(GeolocationContext);
  const currency = geolocation.defaultCurrency;
  const { showDestination = false } = otherProps;

  const {
    cruiseCode,
    available,
    days,
    content,
    departurePort,
    arrivalPort,
    destinationId,
    destinationName,
    cruiseType,
    ...othersProps
  } = hit;
  const priceFrom = othersProps[`priceFrom_${currency}`];
  const formattedPriceFrom =
    (priceFrom && formatCurrency(priceFrom, currency, language.code)) || null;

  const {
    shipName,
    cardImageJson,
    starText,
    shipPassengerCount,
    detailUrl,
    specialType
  } = content || {};
  const departureDate = departurePort?.itineraryDate.replace(/Z$/, "");
  const relativeDetailUrl = detailUrl
    ?.replace("https://www.silversea.com", "")
    ?.replace("/en", "");

  const departurePortProps = {
    name: departurePort?.city,
    portCode: departurePort?.cityCode,
    worldLocation: computeWorldLocationLabel(
      departurePort?.region,
      departurePort?.country
    )
  };
  const arrivalPortProps = {
    name: arrivalPort.city,
    portCode: arrivalPort.cityCode,
    worldLocation: computeWorldLocationLabel(
      arrivalPort.region,
      arrivalPort.country
    )
  };
  const image = JSON.parse(cardImageJson || "{}") as AlgoliaCruiseHitImage;

  const cruiseInfo = {
    voyageCode: cruiseCode,
    image: image,
    priceFrom: formattedPriceFrom,
    departureDate: departureDate,
    shipName: shipName,
    passengerCount: shipPassengerCount || 0,
    durationInDays: days || 0,
    starText: starText,
    departurePort: departurePortProps,
    arrivalPort: arrivalPortProps,
    available: available,
    detailUrl: relativeDetailUrl,
    specialType: specialType,
    destinationId: destinationId,
    destinationName: destinationName,
    isExpedition: cruiseType == "Expedition",
    showDestination: showDestination
  };

  return (
    <ErrorBoundary>
      <CruiseCard {...cruiseInfo} />
    </ErrorBoundary>
  );
};

export default CruiseCardResult;
